import { useQuery } from "@tanstack/react-query";
import {
  FC,
  ReactElement,
  ReactNode,
  createContext,
  useCallback,
  useEffect,
  useState,
} from "react";

import { hasRanaOrganisation, retrieveTenantAndOrganisation } from "@nens/ranalib";
import { compareUUIDs } from "@nens/shared-utils";
import { Organisation } from "@nens/threedi/openapi-client";

import { getOrganisationsApi } from "../apiConfiguration";

import { useTenantSwitch } from "./tenant-switch-context";

// Note the hack with null as User is acceptable because the context will only be
// supplied by the Provider in situations where profile is a User, not null.
// Saves a lot of '!' use everywhere the context is actually used.

interface OrganisationContextProps {
  organisations: Organisation[];
  organisation: Organisation;
  setOrganisation: (org: Organisation) => void;
}

export const OrganisationContext = createContext<OrganisationContextProps>({
  organisations: [],
  organisation: null as unknown as Organisation,
  setOrganisation: () => null,
});

interface OrganisationProviderProps {
  loginUrl: string;
  fetchingComponent?: ReactElement;
  children: ReactNode;
}

export const OrganisationProvider: FC<OrganisationProviderProps> = ({
  loginUrl,
  fetchingComponent,
  children,
}) => {
  // This provider tracks the current HCC organisation. But if it changes,
  // sometimes the tenant also needs to be changed. That is what
  // switchOrganisation is for.
  const [organisation, setOrganisation] = useState<Organisation | null>(null);
  const { tenant, switchOrganisation } = useTenantSwitch();

  const selectOrganisation = useCallback(
    (nextOrganisation: Organisation) => {
      switchOrganisation(nextOrganisation);

      // Cross-tenant selection is restored after TenantProvider remounts
      // so we only do it here for the case that tenant doesn't change.
      if (!nextOrganisation.ranaTenantId || nextOrganisation.ranaTenantId === tenant) {
        setOrganisation(nextOrganisation);
      }
    },
    [switchOrganisation, tenant]
  );

  const organisationsApi = getOrganisationsApi();

  const { status, data } = useQuery({
    queryKey: ["all-organisations"],
    queryFn: () =>
      organisationsApi.organisationsList({
        validContractsOnly: true,
        limit: 9999,
      }),
  });

  // All organisations the user has access to.
  const organisations: Organisation[] = data?.results ?? [];

  // Previously chosen tenant and organisation from localstorage.
  const storedTenantAndOrganisation = retrieveTenantAndOrganisation();

  // Only use the organisation stored in localstorage if it has
  // the same tenant as our current tenant.
  const storedOrganisationId =
    hasRanaOrganisation() && storedTenantAndOrganisation.tenant === tenant
      ? storedTenantAndOrganisation.tenantOrganisation
      : null;
  const storedOrganisation = storedOrganisationId
    ? organisations.find((org) => compareUUIDs(org.uniqueId, storedOrganisationId))
    : null;

  // Use the current organisation if it's still in the list of all organisations or
  // the stored organisation, or the first from the list as default.
  const currentOrganisation =
    (organisation && (organisation.ranaTenantId === null || organisation.ranaTenantId === tenant)
      ? organisations.find((org) => org.uniqueId === organisation.uniqueId)
      : undefined) ??
    storedOrganisation ??
    organisations[0];

  // Set organisation after loading the list of organisations is successful.
  useEffect(() => {
    if (status === "success" && currentOrganisation && currentOrganisation !== organisation) {
      setOrganisation(currentOrganisation);
    }
  }, [currentOrganisation, organisation, status]);

  if (status === "error") {
    window.location.replace(loginUrl);
    return null;
  }

  if (status === "pending") {
    return fetchingComponent ?? null;
  }

  if (!data || !data.results || !data.results.length) {
    window.location.replace(loginUrl);
    return null;
  }

  if (status === "success") {
    return (
      <OrganisationContext.Provider
        value={{
          organisations,
          organisation: currentOrganisation as Organisation,
          setOrganisation: selectOrganisation,
        }}
      >
        {children}
      </OrganisationContext.Provider>
    );
  }

  // Should never happen.
  return <p>Failed to load Rana HCC.</p>;
};
